// src/pages/AdminPage/index.tsx
import React, { useState, useCallback } from "react";

// Hooks
import { useToast } from "./hooks/useToast";
import { useAdminStats } from "./hooks/useAdminStats";

// Components
import AdminSidebar from "./components/AdminSidebar";
import AdminTopbar from "./components/AdminTopbar";
import Toast from "./components/Toast";
import ConfirmModal from "./components/ConfirmModal";

// Tabs
import DashboardTab from "./tabs/DashboardTab";
import UsersTab from "./tabs/UsersTab";
import QuestionsTab from "./tabs/QuestionsTab";
import TopicsTab from "./tabs/TopicsTab";
import NotificationsTab from "./tabs/NotificationsTab";
import VideosTab from "./tabs/VideosTab";
import ReportsTab from "./tabs/ReportsTab";

// Modals
import EditUserModal from "./modals/EditUserModal";
import EditTopicModal from "./modals/EditTopicModal";
import EditNotificationModal from "./modals/EditNotificationModal";
import EditVideoModal from "./modals/EditVideoModal";

// API
import { adminAPI } from "./utils/adminApi";

// Types
import type {
  AdminTab,
  UserItem,
  TopicItem,
  NotificationItem,
  VideoItem,
  QuestionReportStatus,
} from "./utils/adminTypes";

// Styles
import "./styles/adminAnimations.css";

const AdminPage: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Toast
  const { toast, showToast } = useToast();
  const onError = useCallback(
    (msg: string) => showToast(msg, "error"),
    [showToast],
  );

  // Dashboard stats
  const {
    stats,
    growth,
    growthPeriod,
    setGrowthPeriod,
    loading: statsLoading,
    reload: reloadStats,
  } = useAdminStats(onError);

  // ─── Modals state ─────────────────────────────────────────────────
  const [editUser, setEditUser] = useState<UserItem | null>(null);
  const [editTopic, setEditTopic] = useState<TopicItem | null>(null);
  const [topicModalOpen, setTopicModalOpen] = useState(false);
  const [editNotification, setEditNotification] =
    useState<NotificationItem | null>(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [editVideo, setEditVideo] = useState<VideoItem | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Confirmation modals
  const [confirmDeleteUser, setConfirmDeleteUser] = useState<string | null>(
    null,
  );
  const [confirmDeleteQuestion, setConfirmDeleteQuestion] = useState<
    number | null
  >(null);
  const [confirmDeleteTopic, setConfirmDeleteTopic] = useState<number | null>(
    null,
  );
  const [confirmDeleteNotification, setConfirmDeleteNotification] = useState<
    number | null
  >(null);
  const [confirmDeleteVideo, setConfirmDeleteVideo] = useState<number | null>(
    null,
  );

  // ─── Force reload tabs ────────────────────────────────────────────
  const triggerReload = () => setReloadKey((k) => k + 1);

  // ─── User actions ─────────────────────────────────────────────────
  const handleUpdateUser = async (id: string, data: Partial<UserItem>) => {
    try {
      await adminAPI.updateUser(id, data);
      showToast("Foydalanuvchi yangilandi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
      throw new Error("Update failed");
    }
  };

  const handleDeleteUser = async () => {
    if (!confirmDeleteUser) return;
    try {
      await adminAPI.deleteUser(confirmDeleteUser);
      showToast("Foydalanuvchi o'chirildi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    } finally {
      setConfirmDeleteUser(null);
    }
  };

  // ─── Question actions ─────────────────────────────────────────────
  const handleDeleteQuestion = async () => {
    if (!confirmDeleteQuestion) return;
    try {
      await adminAPI.deleteQuestion(confirmDeleteQuestion);
      showToast("Savol o'chirildi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    } finally {
      setConfirmDeleteQuestion(null);
    }
  };

  // ─── Topic actions ────────────────────────────────────────────────
  const handleSaveTopic = async (id: number | null, data: any) => {
    try {
      if (id) {
        await adminAPI.updateTopic(id, data);
        showToast("Mavzu yangilandi");
      } else {
        await adminAPI.createTopic(data);
        showToast("Mavzu yaratildi");
      }
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
      throw new Error("Save failed");
    }
  };

  const handleDeleteTopic = async () => {
    if (!confirmDeleteTopic) return;
    try {
      await adminAPI.deleteTopic(confirmDeleteTopic);
      showToast("Mavzu o'chirildi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    } finally {
      setConfirmDeleteTopic(null);
    }
  };

  // ─── Notification actions ────────────────────────────────────────
  const handleSaveNotification = async (id: number | null, data: any) => {
    try {
      if (id) {
        await adminAPI.updateNotification(id, data);
        showToast("Bildirishnoma yangilandi");
      } else {
        await adminAPI.createNotification(data);
        showToast("Bildirishnoma yaratildi");
      }
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
      throw new Error("Save failed");
    }
  };

  const handleDeleteNotification = async () => {
    if (!confirmDeleteNotification) return;
    try {
      await adminAPI.deleteNotification(confirmDeleteNotification);
      showToast("Bildirishnoma o'chirildi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    } finally {
      setConfirmDeleteNotification(null);
    }
  };

  // ─── Video actions ────────────────────────────────────────────────
  const handleSaveVideo = async (id: number | null, data: any) => {
    try {
      if (id) {
        await adminAPI.updateVideo(id, data);
        showToast("Video yangilandi");
      } else {
        await adminAPI.createVideo(data);
        showToast("Video yaratildi");
      }
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
      throw new Error("Save failed");
    }
  };

  const handleDeleteVideo = async () => {
    if (!confirmDeleteVideo) return;
    try {
      await adminAPI.deleteVideo(confirmDeleteVideo);
      showToast("Video o'chirildi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    } finally {
      setConfirmDeleteVideo(null);
    }
  };

  // ─── Report actions ───────────────────────────────────────────────
  const handleReportStatusChange = async (
    id: number,
    status: QuestionReportStatus,
  ) => {
    try {
      await adminAPI.updateReportStatus(id, status);
      showToast("Shikoyat statusi yangilandi");
      triggerReload();
    } catch {
      showToast("Xatolik yuz berdi", "error");
    }
  };

  // ─── Refresh handler ──────────────────────────────────────────────
  const handleRefresh = () => {
    if (tab === "dashboard") reloadStats();
    else triggerReload();
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0E1A",
        color: "#E2E8F0",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        display: "flex",
      }}>
      <AdminSidebar
        tab={tab}
        setTab={setTab}
        isMobileOpen={sidebarOpen}
        onMobileClose={() => setSidebarOpen(false)}
      />

      <div
        className="main-content"
        style={{
          flex: 1,
          marginLeft: 240,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}>
        <AdminTopbar
          tab={tab}
          onMobileMenuOpen={() => setSidebarOpen(true)}
          onRefresh={handleRefresh}
        />

        <div
          className="admin-content-area"
          style={{ flex: 1, padding: "24px", overflowY: "auto" }}>
          {tab === "dashboard" && (
            <DashboardTab
              stats={stats}
              growth={growth}
              growthPeriod={growthPeriod}
              setGrowthPeriod={setGrowthPeriod}
              loading={statsLoading}
              setTab={setTab}
            />
          )}

          {tab === "users" && (
            <UsersTab
              key={`users-${reloadKey}`}
              onEdit={setEditUser}
              onDelete={setConfirmDeleteUser}
              onError={onError}
            />
          )}

          {tab === "questions" && (
            <QuestionsTab
              key={`questions-${reloadKey}`}
              onError={onError}
              onSuccess={(msg) => showToast(msg, "success")}
            />
          )}

          {tab === "topics" && (
            <TopicsTab
              key={`topics-${reloadKey}`}
              onCreate={() => {
                setEditTopic(null);
                setTopicModalOpen(true);
              }}
              onEdit={(topic) => {
                setEditTopic(topic);
                setTopicModalOpen(true);
              }}
              onDelete={setConfirmDeleteTopic}
              onError={onError}
            />
          )}

          {tab === "notifications" && (
            <NotificationsTab
              key={`notifications-${reloadKey}`}
              onCreate={() => {
                setEditNotification(null);
                setNotificationModalOpen(true);
              }}
              onEdit={(n) => {
                setEditNotification(n);
                setNotificationModalOpen(true);
              }}
              onDelete={setConfirmDeleteNotification}
              onError={onError}
            />
          )}

          {tab === "videos" && (
            <VideosTab
              key={`videos-${reloadKey}`}
              onCreate={() => {
                setEditVideo(null);
                setVideoModalOpen(true);
              }}
              onEdit={(v) => {
                setEditVideo(v);
                setVideoModalOpen(true);
              }}
              onDelete={setConfirmDeleteVideo}
              onError={onError}
            />
          )}

          {tab === "reports" && (
            <ReportsTab
              key={`reports-${reloadKey}`}
              onStatusChange={handleReportStatusChange}
              onError={onError}
            />
          )}
        </div>
      </div>

      {/* ─── Modals ──────────────────────────────────────────────── */}
      <EditUserModal
        user={editUser}
        onClose={() => setEditUser(null)}
        onSave={handleUpdateUser}
      />

      <EditTopicModal
        topic={editTopic}
        open={topicModalOpen}
        onClose={() => {
          setTopicModalOpen(false);
          setEditTopic(null);
        }}
        onSave={handleSaveTopic}
      />

      <ConfirmModal
        open={!!confirmDeleteUser}
        title="Foydalanuvchini o'chirish"
        message="Bu foydalanuvchi va uning barcha ma'lumotlari o'chiriladi!"
        onConfirm={handleDeleteUser}
        onCancel={() => setConfirmDeleteUser(null)}
      />

      <ConfirmModal
        open={!!confirmDeleteQuestion}
        title="Savolni o'chirish"
        message="Bu amalni qaytarib bo'lmaydi!"
        onConfirm={handleDeleteQuestion}
        onCancel={() => setConfirmDeleteQuestion(null)}
      />

      <ConfirmModal
        open={!!confirmDeleteTopic}
        title="Mavzuni o'chirish"
        message="Mavzu va unga bog'liq savollar muammoga duch kelishi mumkin!"
        onConfirm={handleDeleteTopic}
        onCancel={() => setConfirmDeleteTopic(null)}
      />

      <EditNotificationModal
        notification={editNotification}
        open={notificationModalOpen}
        onClose={() => {
          setNotificationModalOpen(false);
          setEditNotification(null);
        }}
        onSave={handleSaveNotification}
      />

      <EditVideoModal
        video={editVideo}
        open={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setEditVideo(null);
        }}
        onSave={handleSaveVideo}
        onError={onError}
        onSuccess={(msg) => showToast(msg, "success")}
      />

      <ConfirmModal
        open={!!confirmDeleteNotification}
        title="Bildirishnomani o'chirish"
        message="Bu amalni qaytarib bo'lmaydi!"
        onConfirm={handleDeleteNotification}
        onCancel={() => setConfirmDeleteNotification(null)}
      />

      <ConfirmModal
        open={!!confirmDeleteVideo}
        title="Videoni o'chirish"
        message="Bu amalni qaytarib bo'lmaydi!"
        onConfirm={handleDeleteVideo}
        onCancel={() => setConfirmDeleteVideo(null)}
      />

      {/* ─── Toast ──────────────────────────────────────────────── */}
      <Toast toast={toast} />
    </div>
  );
};

export default AdminPage;
